import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { formatINR } from "@/lib/api";

export default function CartSheet() {
  const { cartOpen, setCartOpen, cart, updateQty, removeFromCart, cartSubtotal, cartCount } = useApp();
  const shipping = cart.reduce((s, it) => s + ((it.product.shipping_price || 0) * it.quantity), 0);

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-yw-bg border-l border-yw-border flex flex-col p-0" data-testid="cart-sheet">
        <SheetHeader className="p-6 border-b border-yw-border">
          <SheetTitle className="font-playfair text-2xl text-yw-ink text-left">Your Bag ({cartCount})</SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-4">
            <ShoppingBag size={36} className="text-yw-gold" />
            <p className="font-playfair text-xl text-yw-ink">Your bag is empty</p>
            <p className="font-inter text-sm text-yw-gray">Discover pieces designed to be loved every day.</p>
            <Link to="/shop" onClick={() => setCartOpen(false)} data-testid="cart-empty-shop-link"
              className="mt-2 px-7 py-3 bg-yw-ink text-white rounded-[14px] text-[11px] tracking-[0.22em] uppercase hover:bg-yw-gold transition-colors">
              Start Shopping
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {cart.map((item) => (
                <div key={item.product.id} className="flex gap-4" data-testid={`cart-item-${item.product.slug}`}>
                  <div className="w-20 h-24 rounded-[14px] overflow-hidden bg-yw-hover flex-shrink-0">
                    <img src={item.product.images[0]} alt={item.product.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <Link to={`/product/${item.product.slug}`} onClick={() => setCartOpen(false)}
                        className="font-playfair text-[15px] text-yw-ink leading-snug hover:text-yw-gold">{item.product.name}</Link>
                      <button onClick={() => removeFromCart(item.product.id)} data-testid={`cart-remove-${item.product.slug}`}
                        className="text-yw-gray hover:text-yw-ink p-1"><X size={14} /></button>
                    </div>
                    <p className="font-inter text-sm text-yw-gray mt-1">{formatINR(item.product.price)}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="inline-flex items-center border border-yw-border rounded-full">
                        <button onClick={() => updateQty(item.product.id, item.quantity - 1)} className="px-2.5 py-1.5 hover:text-yw-gold"
                          data-testid={`cart-qty-dec-${item.product.slug}`}><Minus size={12} /></button>
                        <span className="px-2 text-sm font-inter">{item.quantity}</span>
                        <button onClick={() => updateQty(item.product.id, item.quantity + 1)} className="px-2.5 py-1.5 hover:text-yw-gold"
                          data-testid={`cart-qty-inc-${item.product.slug}`}><Plus size={12} /></button>
                      </div>
                      <p className="font-inter text-sm font-medium text-yw-ink">{formatINR(item.product.price * item.quantity)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-yw-border p-6 space-y-3">
              <div className="flex items-center justify-between text-sm font-inter">
                <span className="text-yw-gray">Subtotal</span>
                <span className="text-yw-ink font-medium">{formatINR(cartSubtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm font-inter">
                <span className="text-yw-gray">Shipping</span>
                <span className="text-yw-ink">{shipping === 0 ? "Complimentary" : formatINR(shipping)}</span>
              </div>
              <div className="flex items-center justify-between font-playfair text-lg pt-3 border-t border-yw-border">
                <span>Total</span>
                <span>{formatINR(cartSubtotal + shipping)}</span>
              </div>
              <Link to="/checkout" onClick={() => setCartOpen(false)} data-testid="cart-checkout-btn"
                className="block text-center w-full py-4 bg-yw-ink text-white rounded-[14px] text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-yw-gold transition-colors">
                Proceed to Checkout
              </Link>
              <Link to="/cart" onClick={() => setCartOpen(false)} data-testid="cart-view-link"
                className="block text-center w-full py-3 text-yw-gray text-xs tracking-[0.18em] uppercase hover:text-yw-gold transition-colors">
                View Full Bag
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
